// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CoolingSystemType } from 'src/app/constants/portal-cooling-companion/cooling-companion';
import { MapApiService } from '../../shared/services/map-api.service';
import { apiCtCoolingSystemGetCoolingTypeGet } from '../../../api/remote-api/.cooling.generated/fn/ct-cooling-system/api-ct-cooling-system-get-cooling-type-get';
import { ExtendedComponentModel } from 'src/app/_interfaces/component-model-extension';
import { apiCtCoolingSystemGetWcMaxValuesWithLastUpdatedDateTimePost } from '../../../api/remote-api/.cooling.generated/fn/ct-cooling-system/api-ct-cooling-system-get-wc-max-values-with-last-updated-date-time-post';
import { apiCtCoolingSystemGetGraphDataPointsForWcPost } from '../../../api/remote-api/.cooling.generated/fn/ct-cooling-system/api-ct-cooling-system-get-graph-data-points-for-wc-post';
import { apiCtCoolingSystemGetCalendarLogCountGet } from '../../../api/remote-api/.cooling.generated/fn/ct-cooling-system/api-ct-cooling-system-get-calendar-log-count-get';
import {
  SystemStatus,
  SystemStatusCollection,
} from '../interfaces/system-status.interface';
import moment from 'moment';
import { formatDate } from '@angular/common';
import { WcMaxValueResponse } from 'src/app/api/remote-api/.cooling.generated/models/wc-max-value-response';
import { WcMaxValueRequest } from 'src/app/api/remote-api/.cooling.generated/models/wc-max-value-request';
import { SystemTypeConstants } from 'src/app/api/remote-api/.cooling.generated/models/system-type-constants';
import { WcGraphRequest } from 'src/app/api/remote-api/.cooling.generated/models/wc-graph-request';
import { GraphData } from 'src/app/api/remote-api/.cooling.generated/models/graph-data';
import { LocalDateFormat } from 'src/app/_interfaces/data/local-data-format-model';
import { CoolingKeyValueData } from '../interfaces/cooling-key-value-data.interface';
import { MeasurementRecord } from 'src/app/api/remote-api/.cooling.generated/models/measurement-record';
@Injectable({
  providedIn: 'root',
})
export class CoolingCompanionService {
  mappedEndpoints = new Map();
  coolingGraphData = this.mapApiService.mapEndpoint(
    apiCtCoolingSystemGetGraphDataPointsForWcPost,
  );
  systemStatusData = this.mapApiService.mapEndpoint(
    apiCtCoolingSystemGetWcMaxValuesWithLastUpdatedDateTimePost,
  );
  coolingType = this.mapApiService.mapEndpoint(
    apiCtCoolingSystemGetCoolingTypeGet,
  );
  calendarLogCount = this.mapApiService.mapEndpoint(
    apiCtCoolingSystemGetCalendarLogCountGet,
  );

  constructor(private mapApiService: MapApiService) {}

  private getCoolingType(
    systemSerialNumber: string,
    systemMaterialNumber: number,
  ): Observable<SystemTypeConstants> {
    return this.coolingType.request({
      serialNumber: systemSerialNumber,
      materialNumber: systemMaterialNumber,
    });
  }

  getCalendarLogCount(
    systemSerialNumber: string,
    systemMaterialNumber: number,
  ): Observable<Record<string, number>> {
    return this.calendarLogCount
      .request({
        serialNumber: systemSerialNumber,
        materialNumber: systemMaterialNumber,
      })
      .pipe(
        map((res) => {
          const activeDays: Record<string, number> = {};
          res.forEach((item) => {
            activeDays[item.date] = item.count ?? 0;
          });
          return activeDays;
        }),
      );
  }

  updateCoolingSystemDisplayName(
    component: ExtendedComponentModel,
    systemSerialNumber: string,
    systemMaterialNumber: number,
  ): void {
    this.getCoolingType(systemSerialNumber, systemMaterialNumber).subscribe(
      (coolingSystemType: CoolingSystemType) => {
        switch (coolingSystemType) {
          case CoolingSystemType.AIR:
            component.displayName = `Cooling System (Air)`;
            break;
          case CoolingSystemType.WATER:
            component.displayName = `Cooling System (Air, Water)`;
            break;
          case CoolingSystemType.NONE:
            component.displayName = `Cooling System ( - )`;
            break;
          case CoolingSystemType.UNKNOWN:
            component.displayName = `Cooling System ( New )`;
            break;
        }
      },
    );
  }

  getsystemStatusData(
    requestBody: WcMaxValueRequest,
  ): Observable<SystemStatusCollection> {
    return this.systemStatusData.request({ body: requestBody }).pipe(
      map((res) => {
        const response = res as WcMaxValueResponse;
        return {
          latestDate: this.getTimeFromNow(response.latestDate ?? ''),
          timeZoneOffset: response.timeZoneOffset ?? 0,
          SystemStatusList: this.mapToSystemStatusList(
            response.measurementRecords ?? [],
          ),
        };
      }),
    );
  }

  private mapToSystemStatusList(records: MeasurementRecord[]): SystemStatus[] {
    const systemStatusList: SystemStatus[] = [];

    for (const record of records) {
      const isWaterFlow = record.measurementName === 'WaterFlow';

      const measurementUnit = this.getMeasurementUnit(isWaterFlow);
      const measurementName = this.formatMeasurementName(
        record.measurementName ?? '',
      );
      const maxMeasurementValue = this.getMaxMeasurementValue(
        record,
        isWaterFlow,
      );
      const lastUpdatedDateTime = this.getTimeFromNow(
        record.lastUpdatedDateTime ?? '',
      );

      const systemStatus: SystemStatus = {
        measurementName,
        maxMeasurementValue,
        measurementUnit,
        lastUpdatedDateTime,
      };

      systemStatusList.push(systemStatus);
    }

    return systemStatusList;
  }

  formatEventDateTime(dateTime: string, dateFormat: LocalDateFormat): string {
    const [date, time] = dateTime.split('T');
    if (!date || !time) {
      return dateTime;
    }
    const shuiLocale = formatDate(date, dateFormat.longFormat, 'en-US');
    const formattedDate = shuiLocale.replace('T', ' ');
    const formattedTime = time.replace('Z', '');
    return formattedDate + '\n' + formattedTime;
  }

  private formatMeasurementName(name: string): string {
    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private getMaxMeasurementValue(
    record: MeasurementRecord,
    isWaterFlow: boolean,
  ): number | string {
    if (
      record.maxMeasurementValue === null ||
      record.maxMeasurementValue === undefined
    ) {
      return '-';
    }

    if (isWaterFlow) {
      return record.maxMeasurementValue;
    }

    return Number((record.maxMeasurementValue / 1000).toFixed(1));
  }

  private getMeasurementUnit(isWaterFlow: boolean): string {
    if (isWaterFlow) {
      return 'l/h';
    }
    return '°C';
  }
  getTimeFromNow(dateTime: string): string {
    if (!dateTime) {
      return 'unknown';
    }

    const givenTime = moment.utc(dateTime);
    if (!givenTime.isValid()) {
      return 'unknown';
    }

    const nowUtc = moment.utc();
    return givenTime.from(nowUtc);
  }

  convertCoolingResponseToKeyValue(response: GraphData): CoolingKeyValueData {
    const yAxesGraphData: any = {};
    const measurementParameters: any = {};

    response.yAxesGraphData.forEach((typeItem) => {
      yAxesGraphData[typeItem.name] = {
        min: typeItem.min,
        max: typeItem.max,
        interval: typeItem.interval,
      };
    });

    response.measurements.forEach((dataItem) => {
      measurementParameters[dataItem.name] = dataItem.values;
    });

    // Add eventTimeSeries as a key
    const result: CoolingKeyValueData = {
      yAxesGraphData,
      measurementParameters,
    };

    return result;
  }

  getCoolingGraphData(
    graphRequestBody: WcGraphRequest,
  ): Observable<CoolingKeyValueData> {
    return this.coolingGraphData.request({ body: graphRequestBody }).pipe(
      map((res) => {
        // Convert readonly arrays to mutable arrays for compatibility with GraphData type
        const mutableResponse: GraphData = {
          ...res,
          measurements: res.measurements.map((measurement: any) => ({
            ...measurement,
            values: Array.from(measurement.values),
          })),
          yAxesGraphData: res.yAxesGraphData
            ? res.yAxesGraphData.map((yAxis: any) => ({ ...yAxis }))
            : [],
        };
        const formattedGraphData =
          this.convertCoolingResponseToKeyValue(mutableResponse);
        return formattedGraphData;
      }),
    );
  }
}
